import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Element } from 'react-scroll';

import styles from '@components/join/join.module.scss';
import { useCheckDuplicationId } from '@repository/register/useRegister';
import LabelInput from '@components/labelInput';
import { idValidator, valueRequired } from '@utils/validators/validators';
import { ERROR_TEXT, GUIDE_TEXT, SUCCESS_TEXT } from '@utils/validators/text';
import classnames from 'classnames/bind';
const cx = classnames.bind(styles);

interface IUserId {
  prefixName: string;
  idState: 'Duplication' | 'Checked' | null;
  setIdState: (idState: 'Duplication' | 'Checked' | null) => void;
}

const UserId = ({ prefixName, idState, setIdState }: IUserId) => {
  const {
    watch,
    register,
    formState: { errors },
    trigger,
    setError,
  } = useFormContext();
  const userId = watch(prefixName);
  const { mutateAsync: checkDuplicationId } = useCheckDuplicationId();

  const onClickRequestDupID = async () => {
    const isSuccess = await trigger('userId');

    if (isSuccess) {
      const { isDuplicate } = await checkDuplicationId(userId);
      setIdState(isDuplicate ? 'Duplication' : 'Checked');
      if (isDuplicate) {
        setError('userId', { message: ERROR_TEXT.ID_DUPLICATE });
      }
      return;
    }
  };

  const getDisabled = () => {
    if (!userId) return true;
    if (idState === 'Checked') return true;
    if (idState === 'Duplication') return true;
    if (errors.userId?.message === ERROR_TEXT.ID_DUPLICATE_REQUIRED) return false;
    return !!errors.userId;
  };

  useEffect(() => {
    setIdState(null);
  }, [userId]);

  return (
    <Element name={prefixName} className={cx('infoWrap')}>
      <LabelInput
        {...register(prefixName, {
          ...valueRequired,
          validate: (value: string) => idValidator(value),
        })}
        type="text"
        minLength={5}
        maxLength={20}
        className={cx('input', { error: errors.userId, success: !errors.userId && idState === 'Checked' })}
        placeholder={'아이디'}
        isvalue={userId}
        tabIndex={6}
      />
      {errors.userId && <div className={cx('infoText', 'error')}>{errors.userId.message as string}</div>}
      {userId && !errors.userId && idState === 'Checked' ? (
        <div className={cx('infoText', 'success')}>{SUCCESS_TEXT.ID}</div>
      ) : (
        <div className={cx('infoText')}>{GUIDE_TEXT.ID}</div>
      )}
      <span className={cx('btnArea')}>
        <button type="button" className={cx('btnInfo')} disabled={getDisabled()} onClick={onClickRequestDupID}>
          중복확인
        </button>
      </span>
    </Element>
  );
};

export default UserId;
